/* FELVO Family — local pet profiles, v2 "keepsake cards".
   Storage: localStorage "felvo_pets_v1" = [{ id, name,
   kind: "photo"|"avatar"|"emoji", value, species?, breed?, birthday? }]
   — kind "avatar" renders as a MONOGRAM medallion; value = finish name
     (porcelain|espresso|gold|olive|sand|noir). Legacy values a1..a6 from v1
     normalize at render time, so no stored data is migrated or rewritten.
   — photo value is a canvas-resized (max 512px) JPEG data URL.
   Touchpoints: header chip, hero greeting, PDP multi-select "Shopping for"
   picker (properties[For] = joined names), and the /pages/my-pets manager.
   User-entered text only ever reaches the DOM through textContent — never
   innerHTML. */
(function () {
  "use strict";

  var KEY = "felvo_pets_v1";
  var MAX_PHOTO = 512;

  var FINISHES = ["porcelain", "espresso", "gold", "olive", "sand", "noir"];
  var LEGACY_FINISH = { a1: "porcelain", a2: "espresso", a3: "olive", a4: "noir", a5: "sand", a6: "gold" };
  var SPECIES_EMOJI = { cat: "🐱", dog: "🐶", bird: "🦜", rabbit: "🐰", small: "🐹", aquatic: "🐠", other: "🐾" };

  /* ---- storage (defensive: bad JSON or blocked storage → empty list) ----- */
  function isPet(p) {
    return (
      p && typeof p === "object" &&
      typeof p.name === "string" && p.name.length > 0 &&
      (p.kind === "photo" || p.kind === "avatar" || p.kind === "emoji") &&
      typeof p.value === "string" && p.value.length > 0
    );
  }
  function loadPets() {
    try {
      var arr = JSON.parse(window.localStorage.getItem(KEY) || "[]");
      return Array.isArray(arr) ? arr.filter(isPet) : [];
    } catch (e) {
      return [];
    }
  }
  function savePets(pets) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(pets));
      return true;
    } catch (e) {
      return false;
    }
  }
  function uid() {
    return "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  /* ---- DOM + i18n helpers ------------------------------------------------- */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }
  function fill(tpl, data) {
    var out = tpl || "";
    Object.keys(data || {}).forEach(function (k) {
      out = out.replace("[[" + k + "]]", data[k]);
    });
    return out;
  }
  function isArabic() {
    return (document.documentElement.lang || "").toLowerCase().indexOf("ar") === 0;
  }
  /* Arabic needs one/two/few/many; everything else one/other. The section
     carries a flat attribute per form and we fall back to -other. */
  function pluralForm(n) {
    if (!isArabic()) return n === 1 ? "one" : "other";
    if (n === 1) return "one";
    if (n === 2) return "two";
    var m = n % 100;
    if (m >= 3 && m <= 10) return "few";
    return "many";
  }
  function pluralTpl(getAttr, base, n) {
    return getAttr(base + "-" + pluralForm(n)) || getAttr(base + "-other") || "";
  }
  function firstGrapheme(name) {
    var arr;
    try { arr = Array.from(name.trim()); } catch (e) { arr = [name.trim().charAt(0)]; }
    var ch = arr[0] || "?";
    try { return ch.toLocaleUpperCase(); } catch (e) { return ch.toUpperCase(); }
  }
  function normFinish(value) {
    if (FINISHES.indexOf(value) >= 0) return value;
    return LEGACY_FINISH[value] || "porcelain";
  }
  /* "Luna", "Luna & Mochi", "Luna, Mochi & Biscuit" — joiner/comma localized */
  function joinNames(names, joiner, comma) {
    if (names.length <= 1) return names[0] || "";
    if (names.length === 2) return names[0] + joiner + names[1];
    return names.slice(0, -1).join(comma) + joiner + names[names.length - 1];
  }

  /* ---- Medallion faces ----------------------------------------------------
     One renderer for every touchpoint. px is the disc diameter. */
  function petFace(pet, px) {
    var wrap = el("span", "pet-medal shrink-0");
    wrap.style.width = px + "px";
    wrap.style.height = px + "px";
    if (pet.kind === "photo" && /^data:image\//.test(pet.value)) {
      var img = document.createElement("img");
      img.src = pet.value;
      img.alt = "";
      img.width = px;
      img.height = px;
      img.className = "h-full w-full object-cover";
      wrap.classList.add("pet-medal--photo");
      wrap.appendChild(img);
    } else if (pet.kind === "emoji") {
      wrap.classList.add("pet-medal--porcelain");
      var glyph = el("span", "pet-medal__emoji", pet.value);
      glyph.style.fontSize = Math.round(px * 0.54) + "px";
      wrap.appendChild(glyph);
    } else {
      wrap.classList.add("pet-medal--" + normFinish(pet.value));
      var mono = el("span", "pet-medal__initial", firstGrapheme(pet.name || "?"));
      mono.style.fontSize = Math.round(px * 0.42) + "px";
      wrap.appendChild(mono);
    }
    return wrap;
  }

  /* ---- Birthday math ------------------------------------------------------ */
  function parseBirthday(value) {
    var d = new Date(value + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  }
  function fmtBirthday(value) {
    var d = parseBirthday(value);
    if (!d) return value;
    try {
      return d.toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric" });
    } catch (e) {
      return value;
    }
  }
  function todayMidnight() {
    var now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  /* Whole days until the next occurrence of the birthday (0 = today). */
  function daysToBirthday(value) {
    var b = parseBirthday(value);
    if (!b) return null;
    var today = todayMidnight();
    var next = new Date(today.getFullYear(), b.getMonth(), b.getDate());
    if (next < today) next = new Date(today.getFullYear() + 1, b.getMonth(), b.getDate());
    return Math.round((next - today) / 86400000);
  }
  /* Age in {years} or {months}; null when birthday is unset/invalid/future. */
  function ageParts(value) {
    var b = parseBirthday(value);
    if (!b) return null;
    var today = todayMidnight();
    if (b > today) return null;
    var years = today.getFullYear() - b.getFullYear();
    var monthDelta = today.getMonth() - b.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < b.getDate())) years -= 1;
    if (years >= 1) return { years: years };
    var months = (today.getFullYear() - b.getFullYear()) * 12 + monthDelta;
    if (today.getDate() < b.getDate()) months -= 1;
    return { months: Math.max(0, months) };
  }
  function birthdayLine(getAttr, days) {
    if (days === 0) return getAttr("data-bday-today");
    if (days === 1) return getAttr("data-bday-tomorrow");
    return fill(pluralTpl(getAttr, "data-bday", days), { n: days });
  }

  /* ---- Header chip (rendered only when pets exist) ------------------------ */
  function renderChip() {
    var chip = document.querySelector("[data-pet-chip]");
    if (!chip) return;
    var pets = loadPets();
    chip.textContent = "";
    if (!pets.length) {
      chip.setAttribute("hidden", "");
      return;
    }
    chip.appendChild(petFace(pets[0], 28));
    if (pets.length > 1) {
      chip.appendChild(el("span", "ms-1 text-[11px] font-extrabold text-gold-300", "+" + (pets.length - 1)));
    }
    chip.removeAttribute("hidden");
  }

  /* ---- Hero greeting ("With love, for Luna & Mochi") ---------------------- */
  function renderGreeting() {
    var box = document.querySelector("[data-pet-greeting]");
    if (!box) return;
    var pets = loadPets();
    if (!pets.length) return;
    var target = box.querySelector("[data-pet-greeting-text]") || box;
    var tpl = box.getAttribute("data-greeting-template") || "[[names]]";
    var joiner = box.getAttribute("data-names-joiner") || " & ";
    /* U+2068/U+2069 (FSI/PDI) bidi-isolate each name so Latin names inside
       the RTL Arabic template don't scramble ("Poppy و Mango" was rendering
       as "Poppyg Mango"). Display-only: order properties stay unmarked. */
    var names = pets.map(function (p) { return "\u2068" + p.name + "\u2069"; });
    var text = names[0];
    if (names.length === 2) text = names[0] + joiner + names[1];
    else if (names.length > 2) text = names[0] + joiner + names[1] + " +" + (names.length - 2);
    target.textContent = tpl.replace("[[names]]", text);
    box.removeAttribute("hidden");
  }

  /* ---- PDP "Shopping for" — multi-select → properties[For] ---------------- */
  function initPicker() {
    var box = document.querySelector("[data-pet-picker]");
    var link = document.querySelector("[data-pet-picker-link]");
    if (!box && !link) return;
    var pets = loadPets();
    if (!pets.length) {
      if (link) link.removeAttribute("hidden");
      if (box) box.setAttribute("hidden", "");
      return;
    }
    if (link) link.setAttribute("hidden", "");
    if (!box) return;
    var chipsWrap = box.querySelector("[data-pet-picker-chips]");
    var input = box.querySelector("[data-pet-picker-input]");
    if (!chipsWrap || !input) return;
    var joiner = box.getAttribute("data-names-joiner") || " & ";
    var comma = box.getAttribute("data-names-comma") || ", ";
    chipsWrap.textContent = "";

    var BASE = "pp-pick inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border-none px-3.5 py-2 text-[13px] font-bold ring-1 transition-colors duration-150";
    var ON = ["bg-gold-300/20", "ring-gold-300", "text-cream-bright"];
    var OFF = ["bg-white/10", "ring-white/15", "text-cream"];
    var selected = {};
    var petBtns = [];
    var allBtn = null;

    function paint(btn, on) {
      ON.forEach(function (c) { btn.classList.toggle(c, on); });
      OFF.forEach(function (c) { btn.classList.toggle(c, !on); });
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    }
    function sync() {
      var names = [];
      pets.forEach(function (p, i) {
        paint(petBtns[i], !!selected[p.id]);
        if (selected[p.id]) names.push(p.name);
      });
      if (allBtn) paint(allBtn, names.length === pets.length);
      if (names.length) {
        input.value = joinNames(names, joiner, comma);
        input.disabled = false;
      } else {
        input.value = "";
        input.disabled = true;
      }
    }

    if (pets.length > 1) {
      allBtn = el("button", BASE);
      allBtn.type = "button";
      allBtn.appendChild(el("span", null, box.getAttribute("data-label-all") || "—"));
      allBtn.addEventListener("click", function () {
        var everyOn = pets.every(function (p) { return selected[p.id]; });
        pets.forEach(function (p) { selected[p.id] = !everyOn; });
        sync();
      });
      chipsWrap.appendChild(allBtn);
    }
    pets.forEach(function (pet) {
      var b = el("button", BASE);
      b.type = "button";
      b.appendChild(petFace(pet, 24));
      b.appendChild(el("span", null, pet.name));
      b.addEventListener("click", function () {
        selected[pet.id] = !selected[pet.id];
        sync();
      });
      chipsWrap.appendChild(b);
      petBtns.push(b);
    });

    sync();
    box.removeAttribute("hidden");
  }

  /* ---- Manager (/pages/my-pets) ------------------------------------------- */
  function initManager() {
    var root = document.querySelector("[data-pet-profiles]");
    if (!root || root.__felvoPets) return;
    root.__felvoPets = true;

    function attr(name) { return root.getAttribute(name) || ""; }

    var grid = root.querySelector("[data-pets-grid]");
    var emptyEl = root.querySelector("[data-pets-empty]");
    var addCard = root.querySelector("[data-pet-add-card]");
    var cardTpl = root.querySelector("[data-pet-card-tpl]");
    var familyLine = root.querySelector("[data-family-line]");
    var dialog = root.querySelector("[data-pet-dialog]");
    var form = root.querySelector("[data-pet-form]");
    if (!grid || !cardTpl || !dialog || !form) return;

    var titleEl = dialog.querySelector("[data-pet-dialog-title]");
    var previewEl = dialog.querySelector("[data-pet-preview]");
    var errorEl = dialog.querySelector("[data-pet-error]");
    var nameInput = form.querySelector("[data-pet-name]");
    var breedInput = form.querySelector("[data-pet-breed]");
    var birthdayInput = form.querySelector("[data-pet-birthday]");
    var photoInput = form.querySelector("[data-pet-photo]");
    var photoName = form.querySelector("[data-pet-photo-name]");
    var tabs = Array.prototype.slice.call(form.querySelectorAll("[data-pet-tab]"));
    var panels = Array.prototype.slice.call(form.querySelectorAll("[data-pet-panel]"));

    var editingId = null;
    var photoData = "";
    var activeKind = "emoji";
    var emojiTouched = false;
    var firstPaint = true;

    function showError(msg) {
      if (!errorEl) return;
      if (msg) {
        errorEl.textContent = msg;
        errorEl.removeAttribute("hidden");
      } else {
        errorEl.textContent = "";
        errorEl.setAttribute("hidden", "");
      }
    }

    function checkedValue(name, fallback) {
      var r = form.querySelector('input[name="' + name + '"]:checked');
      return r ? r.value : fallback;
    }
    function checkRadio(name, value) {
      var list = form.querySelectorAll('input[name="' + name + '"]');
      var hit = false;
      Array.prototype.forEach.call(list, function (r) {
        r.checked = r.value === value;
        if (r.checked) hit = true;
      });
      if (!hit && list.length) list[0].checked = true;
    }

    /* Draft = the pet as currently described by the form. */
    function draftPet() {
      var kind = activeKind;
      var value;
      if (kind === "photo") {
        value = photoData;
        if (!value) { kind = "avatar"; value = checkedValue("pet_finish", "porcelain"); }
      } else if (kind === "emoji") {
        value = checkedValue("pet_emoji", "🐱");
      } else {
        value = checkedValue("pet_finish", "porcelain");
      }
      return { name: (nameInput && nameInput.value.trim()) || "?", kind: kind, value: value };
    }
    function renderPreview() {
      if (!previewEl) return;
      previewEl.textContent = "";
      previewEl.appendChild(petFace(draftPet(), 76));
      /* Monogram swatches carry the live initial too */
      var ini = firstGrapheme((nameInput && nameInput.value.trim()) || "?");
      Array.prototype.forEach.call(form.querySelectorAll("[data-finish-initial]"), function (s) {
        s.textContent = ini;
      });
    }

    function setTab(kind) {
      activeKind = kind;
      tabs.forEach(function (t) {
        var on = t.getAttribute("data-pet-tab") === kind;
        t.classList.toggle("bg-espresso-900", on);
        t.classList.toggle("text-cream", on);
        t.classList.toggle("bg-transparent", !on);
        t.classList.toggle("text-espresso-800/60", !on);
        t.setAttribute("aria-selected", on ? "true" : "false");
      });
      panels.forEach(function (p) {
        if (p.getAttribute("data-pet-panel") === kind) p.removeAttribute("hidden");
        else p.setAttribute("hidden", "");
      });
      showError("");
      renderPreview();
    }

    function openDialog(pet) {
      editingId = pet ? pet.id : null;
      emojiTouched = !!(pet && pet.kind === "emoji");
      photoData = pet && pet.kind === "photo" ? pet.value : "";
      if (titleEl) titleEl.textContent = pet ? attr("data-title-edit") : attr("data-title-add");
      if (nameInput) nameInput.value = pet ? pet.name : "";
      if (breedInput) breedInput.value = pet && pet.breed ? pet.breed : "";
      if (birthdayInput) birthdayInput.value = pet && pet.birthday ? pet.birthday : "";
      if (photoInput) photoInput.value = "";
      if (photoName) photoName.textContent = "";
      checkRadio("pet_species", pet && pet.species ? pet.species : "cat");
      checkRadio("pet_finish", pet && pet.kind === "avatar" ? normFinish(pet.value) : "porcelain");
      checkRadio("pet_emoji", pet && pet.kind === "emoji" ? pet.value : "🐱");
      showError("");
      setTab(pet ? (pet.kind === "avatar" ? "avatar" : pet.kind) : "emoji");
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      if (nameInput && !pet) {
        try { nameInput.focus(); } catch (e) { /* noop */ }
      }
    }
    function closeDialog() {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }

    /* ---- keepsake cards ---- */
    function chipEl(text, extra) {
      return el("span", "pp-chip" + (extra ? " " + extra : ""), text);
    }
    function buildCard(pet, index) {
      var node = cardTpl.content.firstElementChild.cloneNode(true);
      node.setAttribute("data-pet-card", "");

      var no = node.querySelector('[data-slot="no"]');
      if (no) no.textContent = fill(attr("data-member-no"), { n: (index < 9 ? "0" : "") + (index + 1) });

      var ribbonRow = node.querySelector('[data-slot="ribbon-row"]');
      var ribbon = node.querySelector('[data-slot="ribbon"]');
      var days = pet.birthday ? daysToBirthday(pet.birthday) : null;
      if (ribbonRow && ribbon && days != null && days <= 30) {
        ribbon.textContent = birthdayLine(attr, days);
        ribbonRow.removeAttribute("hidden");
      }

      var face = node.querySelector('[data-slot="face"]');
      if (face) face.appendChild(petFace(pet, 84));

      var nameEl = node.querySelector('[data-slot="name"]');
      if (nameEl) nameEl.textContent = pet.name;

      var breedEl = node.querySelector('[data-slot="breed"]');
      if (breedEl && pet.breed) {
        breedEl.textContent = pet.breed;
        breedEl.removeAttribute("hidden");
      }

      var chips = node.querySelector('[data-slot="chips"]');
      if (chips) {
        if (pet.species && SPECIES_EMOJI[pet.species]) {
          var label = attr("data-sp-" + pet.species);
          if (label) chips.appendChild(chipEl(SPECIES_EMOJI[pet.species] + " " + label));
        }
        var age = pet.birthday ? ageParts(pet.birthday) : null;
        if (age) {
          var ageText = age.years != null
            ? fill(pluralTpl(attr, "data-age-y", age.years), { n: age.years })
            : (age.months >= 1 ? fill(pluralTpl(attr, "data-age-m", age.months), { n: age.months }) : attr("data-age-new"));
          if (ageText) chips.appendChild(chipEl(ageText));
        }
        if (pet.birthday && (days == null || days > 30)) chips.appendChild(chipEl(fmtBirthday(pet.birthday)));
        if (!chips.childNodes.length) chips.setAttribute("hidden", "");
      }

      /* actions: quiet row ↔ inline confirm row (no window.confirm) */
      var actRow = node.querySelector("[data-act-row]");
      var confirmRow = node.querySelector("[data-confirm-row]");
      var confirmQ = node.querySelector('[data-slot="confirm-q"]');
      var revertTimer = null;
      function showConfirm(on) {
        if (!actRow || !confirmRow) return;
        if (on) {
          if (confirmQ) confirmQ.textContent = fill(attr("data-confirm-q"), { name: pet.name });
          actRow.setAttribute("hidden", "");
          confirmRow.removeAttribute("hidden");
          if (revertTimer) clearTimeout(revertTimer);
          revertTimer = setTimeout(function () { showConfirm(false); }, 6000);
        } else {
          if (revertTimer) clearTimeout(revertTimer);
          revertTimer = null;
          confirmRow.setAttribute("hidden", "");
          actRow.removeAttribute("hidden");
        }
      }
      var editBtn = node.querySelector('[data-act="edit"]');
      if (editBtn) editBtn.addEventListener("click", function () { openDialog(pet); });
      var removeBtn = node.querySelector('[data-act="remove"]');
      if (removeBtn) removeBtn.addEventListener("click", function () { showConfirm(true); });
      var keepBtn = node.querySelector('[data-act="keep"]');
      if (keepBtn) keepBtn.addEventListener("click", function () { showConfirm(false); });
      var confirmBtn = node.querySelector('[data-act="confirm-remove"]');
      if (confirmBtn) confirmBtn.addEventListener("click", function () {
        var next = loadPets().filter(function (p) { return p.id !== pet.id; });
        if (savePets(next)) {
          render();
          renderChip();
        } else {
          showConfirm(false);
        }
      });

      return node;
    }

    function renderFamilyLine(pets) {
      if (!familyLine) return;
      var countEl = familyLine.querySelector("[data-family-count]");
      var bdayEl = familyLine.querySelector("[data-family-bday]");
      if (!pets.length) {
        familyLine.setAttribute("hidden", "");
        return;
      }
      if (countEl) countEl.textContent = fill(pluralTpl(attr, "data-family", pets.length), { n: pets.length });
      var soonest = null;
      pets.forEach(function (p) {
        if (!p.birthday) return;
        var d = daysToBirthday(p.birthday);
        if (d == null || d > 30) return;
        if (!soonest || d < soonest.days) soonest = { name: p.name, days: d };
      });
      if (bdayEl) {
        if (soonest) {
          bdayEl.textContent = fill(attr("data-hero-bday"), { name: soonest.name, when: birthdayLine(attr, soonest.days) });
          bdayEl.removeAttribute("hidden");
        } else {
          bdayEl.textContent = "";
          bdayEl.setAttribute("hidden", "");
        }
      }
      familyLine.removeAttribute("hidden");
    }

    function render() {
      var pets = loadPets();
      Array.prototype.slice.call(grid.querySelectorAll("[data-pet-card]")).forEach(function (c) {
        c.parentNode.removeChild(c);
      });
      pets.forEach(function (pet, i) {
        var card = buildCard(pet, i);
        if (firstPaint) {
          card.classList.add("pp-in");
          card.style.animationDelay = Math.min(i * 55, 330) + "ms";
        }
        grid.insertBefore(card, addCard || null);
      });
      if (emptyEl) {
        if (pets.length) emptyEl.setAttribute("hidden", "");
        else emptyEl.removeAttribute("hidden");
      }
      if (pets.length) grid.removeAttribute("hidden");
      else grid.setAttribute("hidden", "");
      renderFamilyLine(pets);
      firstPaint = false;
    }

    /* Client-side photo resize: max 512px, JPEG q0.82, white matte (no alpha) */
    function handlePhoto(file) {
      if (!file || !window.URL || !window.HTMLCanvasElement) return;
      var url;
      try {
        url = URL.createObjectURL(file);
      } catch (e) {
        showError(attr("data-msg-photo-error"));
        return;
      }
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth || 1;
          var h = img.naturalHeight || 1;
          var scale = Math.min(1, MAX_PHOTO / Math.max(w, h));
          var canvas = document.createElement("canvas");
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));
          var ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          photoData = canvas.toDataURL("image/jpeg", 0.82);
          if (photoName) photoName.textContent = file.name || "";
          showError("");
          renderPreview();
        } catch (e) {
          showError(attr("data-msg-photo-error"));
        }
        URL.revokeObjectURL(url);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        showError(attr("data-msg-photo-error"));
      };
      img.src = url;
    }

    /* ---- wiring ---- */
    Array.prototype.forEach.call(root.querySelectorAll("[data-pet-add]"), function (b) {
      b.addEventListener("click", function () { openDialog(null); });
    });
    tabs.forEach(function (t) {
      t.addEventListener("click", function () { setTab(t.getAttribute("data-pet-tab")); });
    });
    if (photoInput) {
      photoInput.addEventListener("change", function () {
        if (photoInput.files && photoInput.files[0]) handlePhoto(photoInput.files[0]);
      });
    }
    if (nameInput) nameInput.addEventListener("input", renderPreview);
    form.addEventListener("change", function (ev) {
      var t = ev.target;
      if (!t || !t.name) return;
      if (t.name === "pet_emoji") emojiTouched = true;
      if (t.name === "pet_species" && !emojiTouched && SPECIES_EMOJI[t.value]) {
        checkRadio("pet_emoji", SPECIES_EMOJI[t.value] === "🐾" ? "🐱" : SPECIES_EMOJI[t.value]);
      }
      if (t.name === "pet_emoji" || t.name === "pet_finish" || t.name === "pet_species") renderPreview();
    });
    var cancelBtn = form.querySelector("[data-pet-cancel]");
    if (cancelBtn) cancelBtn.addEventListener("click", closeDialog);
    var closeBtn = dialog.querySelector("[data-pet-close]");
    if (closeBtn) closeBtn.addEventListener("click", closeDialog);
    dialog.addEventListener("click", function (ev) {
      if (ev.target === dialog) closeDialog();
    });

    form.addEventListener("submit", function (ev) {
      ev.preventDefault();
      var name = nameInput ? nameInput.value.trim() : "";
      if (!name) {
        showError(attr("data-msg-name-missing"));
        if (nameInput) nameInput.focus();
        return;
      }
      var value = "";
      if (activeKind === "photo") {
        value = photoData;
        if (!value) {
          showError(attr("data-msg-photo-missing"));
          return;
        }
      } else if (activeKind === "emoji") {
        value = checkedValue("pet_emoji", "🐱");
      } else {
        value = checkedValue("pet_finish", "porcelain");
      }
      var pet = { id: editingId || uid(), name: name, kind: activeKind, value: value };
      var species = checkedValue("pet_species", "");
      if (species) pet.species = species;
      var breed = breedInput ? breedInput.value.trim() : "";
      if (breed) pet.breed = breed;
      var bday = birthdayInput ? birthdayInput.value : "";
      if (bday) pet.birthday = bday;
      var pets = loadPets();
      var idx = -1;
      pets.forEach(function (p, i) { if (p.id === pet.id) idx = i; });
      if (idx >= 0) pets[idx] = pet;
      else pets.push(pet);
      if (savePets(pets)) {
        render();
        renderChip();
        closeDialog();
      } else {
        showError(attr("data-msg-storage"));
      }
    });

    render();
  }

  function init() {
    try {
      renderChip();
      renderGreeting();
      initPicker();
      initManager();
    } catch (e) {
      /* never break the page over a pets feature */
    }
  }

  if (document.readyState !== "loading") init();
  else document.addEventListener("DOMContentLoaded", init);

  // Re-init in the Shopify theme editor when sections reload
  document.addEventListener("shopify:section:load", init);
})();
